/**
 * 「放弃」这个动作的**前端侧全部判据与文案**：二次确认、撤销入口、误触保护。
 *
 * ## 为什么单独一个文件（2026-10-17 用户实测）
 *
 * > "孩子打卡这里，孩子说想点完成时，**不小心点了放弃**，这个有什么方案？"
 *
 * 那个页面上「放弃」当时就排在「完成」**正右边**（`ghost` 小按钮，同一行），
 * 一次滑动/连点就能误触；而误触之后**没有任何回程** —— `skipped` 的孩子端只显示
 * 「已放弃」，唯一的恢复手段是家长的 `/undo`，孩子端界面上根本没有那个按钮。
 *
 * 所以本轮三件事（本文件负责前两件的判据，`StudentTodayPage` 负责呈现）：
 *  1. **二次确认**：点「放弃」永远先弹确认（后果写清楚），确认键**不是默认焦点**；
 *  2. **撤销入口**：放弃成功后立刻给一条带「撤销」按钮的 toast（限时），
 *     并且**今天之内**在那一行一直留一个「恢复成未完成」的入口；
 *  3. 把「放弃」挪出主按钮行（见页面里的 `放弃` 分栏）。
 *
 * ## 为什么判据+文案放在这里而不是页面里
 *
 * `apps/web` 的用例**只跑纯函数**（见 `vitest.config.ts`：`environment: 'node'`、
 * 刻意不装 jsdom / @testing-library）。埋在 JSX 里的判定无法被测试覆盖，
 * 而"点了放弃却没弹确认""确认一次却发了两次 POST"正是那种**不报错、只是错**的 bug。
 * 因此：
 *  - 二次确认的**状态机**是下面这个纯 reducer（`reduceSkipFlow`），
 *    页面只是把它接到 React state / mutation 上；
 *  - 文案是下面的常量（测试直接断言"后果那句写了'今天'与'不能再完成'"）。
 */
import type { CheckinStatus } from '@fht/shared';

// ---------------------------------------------------------------- 二次确认的文案

/**
 * 「放弃」二次确认弹窗的**逐字文案**。
 *
 * ⚠️ 后果那一句是**用户明确要求**的（"说清后果：放弃后这一项今天就结束了，
 * 不能再完成"）。改文案时请连着 `skipUndo.test.ts` 里的断言一起想 ——
 * 那几条断言的作用就是防止有人把它悄悄改回含混的"计为未完成"。
 */
export const SKIP_CONFIRM_COPY = {
  title: '放弃这一项？',
  /**
   * 后果：一句话说清"今天就结束了"。
   *
   * ⚠️ 这里是**纯文本**（不是 markdown）：`**加粗**` 会原样显示成星号。
   * 需要强调时靠外层样式（弹窗里那句用的是 `text-slate-700` + 醒目的琥珀色提示条）。
   */
  consequence: '放弃后这一项今天就结束了，今天不能再完成。',
  /** 安慰+回程：说清还能怎么救回来（否则孩子只会更慌）。 */
  undoHint:
    '点错了也不用慌：放弃之后 15 秒内可以直接点「撤销」，今天之内也能在那一行点「恢复成未完成」。',
  /** 确认键（危险动作，**不是**默认焦点）。 */
  confirmLabel: '确定放弃',
  /** 取消键（**默认焦点落在这里**，误触 Enter / 误点都落在"不放弃"上）。 */
  cancelLabel: '还没完，继续做',
} as const;

/** 放弃成功后那条"可撤销" toast 的停留时长（毫秒）。 */
export const SKIP_UNDO_TOAST_MS = 15_000;
/** 普通 toast 的停留时长（与 `Toast.tsx` 的默认值一致，这里显式写出来便于测试对齐）。 */
export const TOAST_DEFAULT_MS = 3_000;

/** 行内「恢复成未完成」入口的文案（孩子端与家长端共用一句话，避免两处说法不一致）。 */
export const RESTORE_ENTRY = {
  label: '恢复成未完成',
  hint: '刚才误点了「放弃」？点这里退回未完成，可以接着做（今天的记录随时能恢复）。',
  /** 家长端（作业页）的说法：那里是家长替孩子把"不做了"收回来。 */
  parentHint: '把这一项退回未完成：孩子端会重新出现，可以接着做。',
} as const;

// ---------------------------------------------------------------- 二次确认的状态机

/** `idle` = 没有弹窗；`confirming` = 弹窗打开、等用户决定；`submitting` = 已经发出请求。 */
export type SkipPhase = 'idle' | 'confirming' | 'submitting';

export interface SkipFlowState {
  phase: SkipPhase;
  /** 正在问的那一条打卡（`idle` 时为 null）。 */
  checkinId: string | null;
  /** 任务名（弹窗副标题用，避免"我在放弃哪一项"这种困惑）。 */
  name: string;
  /** 放弃原因（可选，最多 200 字，与 `skipCheckinSchema` 一致）。 */
  reason: string;
}

export type SkipFlowEvent =
  /** 点了那一行的「放弃」—— **只打开确认，不发请求**。 */
  | { type: 'open'; checkinId: string; name: string }
  | { type: 'reason'; value: string }
  /** 点了取消 / 按 Esc。 */
  | { type: 'cancel' }
  /** 点了「确定放弃」—— **这是唯一会产生请求的事件**。 */
  | { type: 'confirm' }
  /** 请求成功：收起飞窗（调用方随后弹撤销 toast）。 */
  | { type: 'submitted' }
  /** 请求失败：退回 `confirming`，让人可以再点一次。 */
  | { type: 'failed' };

/** reducer 的"副作用"：只有 `requestSkip` 会真的发一次 POST。 */
export type SkipFlowEffect =
  | { type: 'none' }
  | { type: 'requestSkip'; checkinId: string; reason: string };

export const INITIAL_SKIP_FLOW: SkipFlowState = {
  phase: 'idle',
  checkinId: null,
  name: '',
  reason: '',
};

export interface SkipFlowStep {
  state: SkipFlowState;
  effect: SkipFlowEffect;
}

/**
 * 二次确认的**唯一状态机**。
 *
 * 三条被单测钉死的不变式（`skipUndo.test.ts`）：
 *  1. `open` / `reason` / `cancel` **永远**产生 `{type:'none'}` —— 打开确认框、
 *     改原因、关掉它，一次后端请求都不该发（"二次确认不改变后端调用次数"）；
 *  2. 只有 `confirm` 会产生 `requestSkip`，而且**只在 `confirming` 阶段**：
 *     手机上连点两下「确定放弃」不会发两次 POST（第二下发生在 `submitting`）；
 *  3. `failed` 允许重来（退回 `confirming`），`submitted` 才真正收起飞窗 ——
 *     失败时把弹窗收掉会让人以为"放弃成功了"，那正好和本轮的修复方向相反。
 */
export function reduceSkipFlow(state: SkipFlowState, event: SkipFlowEvent): SkipFlowStep {
  switch (event.type) {
    case 'open':
      // 重复点「放弃」只把目标换成新的那一条，不会叠加两次确认
      return {
        state: { phase: 'confirming', checkinId: event.checkinId, name: event.name, reason: '' },
        effect: { type: 'none' },
      };

    case 'reason':
      // 只在弹窗打开时接受输入（避免状态在 idle 时被脏写）
      if (state.phase === 'idle') return { state, effect: { type: 'none' } };
      return { state: { ...state, reason: event.value }, effect: { type: 'none' } };

    case 'cancel':
      return { state: INITIAL_SKIP_FLOW, effect: { type: 'none' } };

    case 'confirm': {
      if (state.phase !== 'confirming' || !state.checkinId) {
        return { state, effect: { type: 'none' } };
      }
      return {
        state: { ...state, phase: 'submitting' },
        effect: { type: 'requestSkip', checkinId: state.checkinId, reason: state.reason },
      };
    }

    case 'submitted':
      return { state: INITIAL_SKIP_FLOW, effect: { type: 'none' } };

    case 'failed':
      // 只有真的提交过才需要退回；其它阶段收到 failed 一律忽略
      if (state.phase !== 'submitting') return { state, effect: { type: 'none' } };
      return { state: { ...state, phase: 'confirming' }, effect: { type: 'none' } };
  }
}

// ---------------------------------------------------------------- 行内「恢复成未完成」

export interface RestoreEntryInput {
  status: CheckinStatus;
  /** 这一条是不是作业项（`node.kind = 'homework'`）。 */
  isHomework: boolean;
  actorKind: 'student' | 'parent';
  /** `checkin.dueDate`（`YYYY-MM-DD`，布置日）。 */
  dueDate: string | null | undefined;
  /** 家庭时区的今天（后端 `/student/today` 的 `localDate`）。 */
  today: string | null | undefined;
}

/**
 * 那一行要不要渲染「恢复成未完成」。
 *
 * ⚠️ 这是**服务端 `decideRestore()` 的镜像，不是权限来源**：
 *    真正的门禁在 `apps/api` 的 `checkins.service.restoreSkippedCheckin()`。
 *    这里只负责"不渲染一个点了会 403 的按钮"（与项目既有约定一致：
 *    前端隐藏只是体验，后端校验才是安全边界）。
 *
 * 镜像的三条与服务端逐条对应：
 *  - 只有 `skipped` 可恢复（别的状态没有可撤销的放弃）；
 *  - 学生：**非作业项** + **只有今天**（隔天的已经进结算，交给家长）；
 *  - 家长：任意（作业页那边的入口另见 `ParentHomeworkPage`）。
 *
 * 边界处理：`today` 拿不到（接口未就绪 / 老后端没这个字段）时**按"不是今天"处理**
 * —— 宁可少给一个入口，也不要把一个注定 403 的按钮摆给孩子。
 */
export function canShowRestoreEntry(input: RestoreEntryInput): boolean {
  if (input.status !== 'skipped') return false;
  if (input.actorKind === 'parent') return true;
  if (input.isHomework) return false;
  if (!input.dueDate || !input.today) return false;
  return input.dueDate === input.today;
}

/** 恢复成功后给用户看的那句话（含"积分有没有被冲正"的诚实说明）。 */
export function restoreSuccessMessage(input: {
  reversedDelta: number;
  actualSeconds: number;
}): string {
  const base = '已恢复成未完成，可以接着做';
  // 放弃一般不产生积分（skipped 不计分），所以这条分支极少走；
  // 一旦走了就说清楚"分退了"，否则家长会觉得账不对。
  if (input.reversedDelta !== 0) return `${base}（已把这笔积分退回：${-input.reversedDelta} 分）`;
  return `${base}。这次真实投入的 ${Math.max(0, Math.round(input.actualSeconds / 60))} 分钟仍然记着，接着计时就行`;
}
